import CurrencyMap from 'country-currency-map';
import { Currency } from 'hydra-data-types';
import { isNonEmptyString } from 'ts-utils';
import { parseCurrency } from '@util/Currency';
import { recordError } from '@util/Errors';
import { ApiClient } from '../api/ApiClient';
import { ActionType } from '../store/ActionTypes';
import { INITIAL_GEOLOCATION_STATE } from '../store/geolocation/GeolocationState';
import { store } from '../store/Store';

const ACCEPTED_COUNTRIES = [
  'AE',
  'AT',
  'AU',
  'BE',
  'BG',
  'BR',
  'CA',
  'CH',
  'CI',
  'CR',
  'CY',
  'CZ',
  'DE',
  'DK',
  'DO',
  'EE',
  'ES',
  'FI',
  'FR',
  'GB',
  'GR',
  'GT',
  'HK',
  'HU',
  'ID',
  'IE',
  'IN',
  'IT',
  'JP',
  'LT',
  'LU',
  'LV',
  'MT',
  'MX',
  'MY',
  'NL',
  'NO',
  'NZ',
  'PE',
  'PH',
  'PL',
  'PT',
  'RO',
  'SE',
  'SG',
  'SI',
  'SK',
  'SN',
  'TH',
  'TT',
  'US',
  'UY',
] as const;

export type CountryCode = (typeof ACCEPTED_COUNTRIES)[number];

/**
 * `country-currency-map`'s static dataset is wrong/incomplete for these codes:
 * - GB: the package keys the UK entry under the non-ISO abbreviation 'UK', so
 *   `getCountryByAbbreviation('GB')` returns undefined.
 * - LT/LV/SK: the dataset predates these countries' euro adoption (2015/2014/2009)
 *   and still hardcodes 'USD'.
 */
const CURRENCY_CORRECTIONS: Partial<Record<CountryCode, Currency>> = {
  GB: Currency.GBP,
  LT: Currency.EUR,
  LV: Currency.EUR,
  SK: Currency.EUR,
};

function currencyForCountry(country: CountryCode): Currency {
  const correction = CURRENCY_CORRECTIONS[country];
  if (correction !== undefined) {
    return correction;
  }
  const countryName = CurrencyMap.getCountryByAbbreviation(country);
  const currencyAbbr =
    countryName !== undefined ? CurrencyMap.getCurrencyAbbreviation(countryName) : undefined;
  return parseCurrency(currencyAbbr ?? '');
}

/**
 * Resolve country/currency from the load balancer via getClientCountry, and dispatch to the store.
 * Used for UX (currency, phone defaults) and WA still gates US-only flows (Medicare / US 65+) on
 * `geolocation.country`. On failure or a missing region we keep INITIAL (AU) so free-access paths
 * stay closed rather than granting on a bad lookup. The backend value can be spoofed off the LB
 * path, so it must not be treated as an auth decision on its own.
 */
async function initialize(): Promise<void> {
  let data = INITIAL_GEOLOCATION_STATE;
  try {
    const { countryCode } = await new ApiClient().getClientCountry();

    // Absent when the request bypassed the load balancer or the region is unknown.
    if (isNonEmptyString(countryCode)) {
      const country = validateCountryCode(countryCode);
      data = country !== undefined ? { country, currency: currencyForCountry(country) } : data;
    } else {
      // Soft-fail to AU with no throw — record so a missing X-Client-Region is visible in Sentry.
      recordError(
        new Error(
          `getClientCountry succeeded without countryCode (value: ${String(countryCode)})`
        )
      );
    }
  } catch (error) {
    recordError(error);
  }

  store.dispatch({ type: ActionType.INITIALIZE_GEOLOCATION, data });
}

function validateCountryCode(countryCode: string): CountryCode | undefined {
  return ACCEPTED_COUNTRIES.includes(countryCode as CountryCode)
    ? (countryCode as CountryCode)
    : undefined;
}

export const GeoLocate = {
  initialize,
};
